import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, Button, Select, Modal, Spin } from 'antd';
import Search from 'antd/es/transfer/search';
import { toast } from 'react-toastify';
import ContentLayout from '../../layout/ContentLayout';
import TableSection from '../../components/tables/TableSection';
import ProtocolExportActions from '../../components/protocols/ProtocolExportActions';
import Spinner from '../../components/elements/Spinner/Spinner';
import { FloatingLabelInput } from '../../components/elements/floatingLabelInput/FloatingLabelInput';
import FloatingMultiSelect from '../../components/protocols/FloatingMultiSelect';
import SubcategoryTab from '../../components/protocols/SubcategoryTab';
import protocolApi from '../../api/protocol-api';
import protocolSubcategoryApi from '../../api/protocol-subcategory-api';
import {
  SUBCATEGORY_TABS,
  WEIGHT_RANGE_OPTIONS,
  labelsFor,
  subcategoryLabels,
  tagsForCategories,
} from '../../utils/protocol-form.util';
import { useProtocolCategories } from '../../hooks/useProtocolCategories';
import { useSubcategoryLookup, toSubcategoryOption } from '../../hooks/useSubcategoryLookup';
import { useInfiniteOptions, withSelectedOption } from '../../hooks/useInfiniteOptions';
import { shrinkText } from '../../utils/shrinkText';
import styles from '../../styles/users.module.scss';

// The list shows one row per protocol (its active version) by default;
// older versions stay reachable here and on the "Verzije" page.
const STATUS_OPTIONS = [
  { value: 'active', label: 'Aktivna verzija' },
  { value: 'archived', label: 'Arhivirane verzije' },
  { value: 'all', label: 'Sve verzije' },
];

// Protocol table cells are cut to 10 characters; the full text shows on hover.
const TABLE_TEXT_MAX_LENGTH = 10;
const shortCell = (value) => {
  const text = String(value ?? '-');
  return <span title={text}>{shrinkText(text, TABLE_TEXT_MAX_LENGTH)}</span>;
};

const badgeStyle = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 500,
};

export default function ProtokoliPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') || 'protokoli';

  const [activeTab, setActiveTab] = useState(initialTab);

  // Protocols tab state
  const [protocols, setProtocols] = useState([]);
  const [protocolsPage, setProtocolsPage] = useState(1);
  const [protocolsTotalPages, setProtocolsTotalPages] = useState(1);
  const [protocolsSearch, setProtocolsSearch] = useState('');
  const [debouncedProtocolsSearch, setDebouncedProtocolsSearch] = useState('');
  const [protocolsLoading, setProtocolsLoading] = useState(true);
  const [subcategoryFilter, setSubcategoryFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [weightFilter, setWeightFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const categoryOptions = useProtocolCategories();
  const categoryLabel = (category) =>
    categoryOptions.find((c) => c.value === category)?.label || category;
  const tabItems = [
    { key: 'protokoli', label: 'Protokoli' },
    ...SUBCATEGORY_TABS.map(({ category, tab }) => ({ key: tab, label: categoryLabel(category) })),
  ];
  const activeSubcategoryTab = SUBCATEGORY_TABS.find((t) => t.tab === activeTab);
  // { protocol, categories, rank } while the "Kategorije i redosled" modal is open.
  const [placementTarget, setPlacementTarget] = useState(null);
  const [savingPlacement, setSavingPlacement] = useState(false);
  const [publishLoadingId, setPublishLoadingId] = useState(null);
  // The list response carries no `days`, so exporting a saved protocol has to
  // fetch the full version first.
  const [exportTarget, setExportTarget] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  // Labels for the tags on the current page, the export target and the
  // Podkategorija filter, plus every item the filter has loaded.
  const { known: knownSubcategories, register: registerSubcategories } = useSubcategoryLookup([
    ...protocols.flatMap((p) => p.subcategories || []),
    ...(exportTarget?.subcategories || []),
    subcategoryFilter,
  ]);
  const subcategoryOptions = useMemo(
    () => Object.values(knownSubcategories),
    [knownSubcategories],
  );

  // Podkategorija filter: the chosen Kategorija's items, or every category's
  // (labelled with their category) when none is chosen.
  const fetchSubcategoryFilterPage = useCallback(
    async (page, limit, search) => {
      const { data, success } = await protocolSubcategoryApi.getAll(page, limit, {
        category: categoryFilter || undefined,
        searchTerm: search || undefined,
      });
      if (!success) return null;
      return { items: data.items.map(toSubcategoryOption), totalPages: data.totalPages };
    },
    [categoryFilter],
  );
  const subcategoryFilterList = useInfiniteOptions(fetchSubcategoryFilterPage, {
    reloadKey: categoryFilter,
  });
  useEffect(() => {
    registerSubcategories(subcategoryFilterList.items);
  }, [subcategoryFilterList.items, registerSubcategories]);
  const subcategoryFilterOptions = withSelectedOption(
    subcategoryFilterList.items,
    knownSubcategories[subcategoryFilter],
  ).map((o) => (categoryFilter ? o : { ...o, label: `${o.label} (${categoryLabel(o.category)})` }));

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedProtocolsSearch(protocolsSearch);
      setProtocolsPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [protocolsSearch]);

  const loadProtocols = useCallback(async () => {
    setProtocolsLoading(true);
    const { data, success, message } = await protocolApi.getAll(protocolsPage, 10, {
      searchTerm: debouncedProtocolsSearch,
      subcategory: subcategoryFilter || undefined,
      category: categoryFilter || undefined,
      weightRange: weightFilter || undefined,
      status: statusFilter || undefined,
    });
    if (success) {
      setProtocols(data.items);
      setProtocolsTotalPages(data.totalPages);
    } else {
      toast.error(message || 'Greška pri učitavanju protokola');
    }
    setProtocolsLoading(false);
  }, [
    protocolsPage,
    debouncedProtocolsSearch,
    subcategoryFilter,
    categoryFilter,
    weightFilter,
    statusFilter,
  ]);

  useEffect(() => {
    if (activeTab === 'protokoli') loadProtocols();
  }, [activeTab, loadProtocols]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    const params = new URLSearchParams(location.search);
    params.set('tab', key);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleTogglePublish = async (protocol) => {
    setPublishLoadingId(protocol._id);
    const { success, message } = await protocolApi.setPublished(
      protocol._id,
      !protocol.isPublished,
    );
    if (success) {
      toast.success(protocol.isPublished ? 'Protokol sakriven' : 'Protokol objavljen');
      loadProtocols();
    } else {
      toast.error(message || 'Greška pri promeni statusa');
    }
    setPublishLoadingId(null);
  };

  const handleSavePlacement = async () => {
    if (!placementTarget) return;
    if (!placementTarget.categories.length) {
      toast.error('Izaberite bar jednu kategoriju');
      return;
    }
    setSavingPlacement(true);
    // A deselected category takes its tag with it (the backend would
    // otherwise re-add the category). Tags are sent only when that happens.
    const { protocol, categories } = placementTarget;
    const kept = tagsForCategories(protocol, categories, subcategoryOptions);
    const { success, message } = await protocolApi.updatePlacement(protocol._id, {
      categories,
      rank: placementTarget.rank ?? null,
      ...(kept.length !== (protocol.subcategories || []).length && { subcategories: kept }),
    });
    if (success) {
      toast.success('Kategorije i redosled sačuvani');
      setPlacementTarget(null);
      loadProtocols();
    } else {
      toast.error(message || 'Greška pri čuvanju');
    }
    setSavingPlacement(false);
  };

  const handleOpenExport = async (protocol) => {
    setExportLoading(true);
    setExportTarget({ _id: protocol._id });
    const { data, success, message } = await protocolApi.getById(protocol._id);
    if (success && data) {
      setExportTarget(data);
    } else {
      toast.error(message || 'Greška pri učitavanju protokola');
      setExportTarget(null);
    }
    setExportLoading(false);
  };

  // Duplication pre-fills the create form instead of writing a copy straight
  // away: `durationDays` and `weightRanges` are create-only, and a
  // protocol is published the moment it is created, so an unreviewed copy
  // would go live immediately.
  const handleDuplicateProtocol = async (protocol) => {
    setDuplicatingId(protocol._id);
    const { data, success, message } = await protocolApi.getById(protocol._id);
    if (success && data) {
      navigate('/protokoli/dodaj', { state: { duplicateFrom: data } });
    } else {
      toast.error(message || 'Greška pri učitavanju protokola');
    }
    setDuplicatingId(null);
  };

  const protocolColumns = [
    { key: 'rank', label: 'Redosled', render: (val) => shortCell(val) },
    { key: 'name', label: 'Naziv', render: (_, row) => shortCell(row.name?.sr || '-') },
    {
      key: 'categories',
      label: 'Kategorije',
      render: (val) => shortCell(labelsFor(val, categoryOptions)),
    },
    {
      key: 'subcategories',
      label: 'Podkategorije',
      render: (val) => shortCell(subcategoryLabels(val, subcategoryOptions)),
    },
    { key: 'durationDays', label: 'Trajanje', render: (val) => shortCell(`${val} dana`) },
    {
      key: 'weightRanges',
      label: 'Opseg težine',
      render: (val) => shortCell(labelsFor(val, WEIGHT_RANGE_OPTIONS)),
    },
    { key: 'versionNumber', label: 'Verzija', render: (val) => shortCell(`v${val}`) },
    {
      key: 'status',
      label: 'Status',
      render: (val) => (
        <span
          style={{
            ...badgeStyle,
            backgroundColor: val === 'active' ? '#e6f4ea' : '#f0f0f0',
            color: val === 'active' ? '#1e7e34' : '#555',
          }}
        >
          {val === 'active' ? 'Aktivna verzija' : 'Arhivirana'}
        </span>
      ),
    },
    {
      key: 'isPublished',
      label: 'Objava',
      render: (val) => (
        <span
          style={{
            ...badgeStyle,
            backgroundColor: val ? '#e6f0ff' : '#fdecea',
            color: val ? '#1d5cbf' : '#c0392b',
          }}
        >
          {val ? 'Objavljen' : 'Sakriven'}
        </span>
      ),
    },
  ];

  const protocolActions = [
    {
      key: 'edit',
      label: 'Izmeni',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Izmeni" />,
      onClick: (item) => navigate(`/protokoli/izmeni/${item._id}`),
      visible: (item) => item.status === 'active',
    },
    {
      key: 'placement',
      label: 'Kategorije i redosled',
      icon: <img src="/assets/icons/edit-icon.svg" alt="Kategorije i redosled" />,
      onClick: (item) =>
        setPlacementTarget({
          protocol: item,
          categories: item.categories || [],
          rank: item.rank ?? null,
        }),
      visible: (item) => item.status === 'active',
    },
    {
      key: 'versions',
      label: 'Verzije',
      icon: <img src="/assets/icons/view-icon.svg" alt="Verzije" />,
      onClick: (item) => navigate(`/protokoli/verzije/${item._id}`),
    },
    {
      key: 'publish',
      label: (item) => (item.isPublished ? 'Sakrij' : 'Objavi'),
      icon: <img src="/assets/icons/verification.svg" alt="Toggle" />,
      onClick: handleTogglePublish,
      disabled: (item) => publishLoadingId === item._id,
      visible: (item) => item.status === 'active',
    },
    {
      key: 'duplicate',
      label: 'Dupliciraj',
      icon: <img src="/assets/icons/copy.svg" alt="Dupliciraj" />,
      onClick: handleDuplicateProtocol,
      disabled: (item) => duplicatingId === item._id,
    },
    {
      key: 'export',
      label: 'Izvezi',
      icon: <img src="/assets/icons/download.svg" alt="Izvezi" />,
      onClick: handleOpenExport,
    },
  ];

  return (
    <>
      <ContentLayout>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems}
          className="medics_tab"
        />

        {activeSubcategoryTab && (
          <SubcategoryTab
            key={activeSubcategoryTab.category}
            category={activeSubcategoryTab.category}
            categoryLabel={categoryLabel(activeSubcategoryTab.category)}
          />
        )}

        {activeTab === 'protokoli' && (
          <div className={styles.wrapper}>
            <div className={styles.filters}>
              <div className={styles.filters__left}>
                <Search
                  placeholder="Pretraži protokole"
                  allowClear
                  enterButton
                  className="search-input"
                  onChange={(e) => setProtocolsSearch(e.target.value)}
                  width="auto"
                />
                <Select
                  allowClear
                  placeholder="Kategorija"
                  style={{ minWidth: 180 }}
                  options={categoryOptions}
                  value={categoryFilter}
                  onChange={(val) => {
                    setCategoryFilter(val);
                    if (val && knownSubcategories[subcategoryFilter]?.category !== val) {
                      setSubcategoryFilter(null);
                    }
                    setProtocolsPage(1);
                  }}
                />
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  placeholder="Podkategorija"
                  style={{ minWidth: 200 }}
                  options={subcategoryFilterOptions}
                  loading={subcategoryFilterList.loading}
                  onSearch={subcategoryFilterList.onSearch}
                  onPopupScroll={subcategoryFilterList.onPopupScroll}
                  value={subcategoryFilter}
                  onChange={(val) => {
                    setSubcategoryFilter(val);
                    setProtocolsPage(1);
                  }}
                />
                <Select
                  allowClear
                  placeholder="Opseg težine"
                  style={{ minWidth: 180 }}
                  options={WEIGHT_RANGE_OPTIONS}
                  value={weightFilter}
                  onChange={(val) => {
                    setWeightFilter(val);
                    setProtocolsPage(1);
                  }}
                />
                <Select
                  placeholder="Status"
                  style={{ minWidth: 180 }}
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setProtocolsPage(1);
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  className="btn-primary"
                  type="primary"
                  onClick={() => navigate('/protokoli/dodaj')}
                >
                  Dodaj protokol
                </Button>
              </div>
            </div>
            {protocolsLoading ? (
              <Spinner size="lg" />
            ) : (
              <TableSection
                columns={protocolColumns}
                actions={protocolActions}
                data={protocols}
                emptyState={{
                  icon: '/assets/icons/no-users.svg',
                  title: 'Nema protokola',
                  description: 'Kreirajte prvi protokol',
                }}
                showPagination={protocols.length > 0}
                currentPage={protocolsPage}
                totalPages={protocolsTotalPages}
                onPageChange={setProtocolsPage}
                onSearch={() => {}}
              />
            )}
          </div>
        )}
      </ContentLayout>

      <Modal
        open={!!placementTarget}
        title={`Kategorije i redosled — ${placementTarget?.protocol.name?.sr || ''}`}
        onCancel={() => setPlacementTarget(null)}
        footer={[
          <Button
            key="cancel"
            className="btn-tertiary"
            onClick={() => setPlacementTarget(null)}
            disabled={savingPlacement}
          >
            Odustani
          </Button>,
          <Button
            key="save"
            type="primary"
            className="btn-primary"
            onClick={handleSavePlacement}
            loading={savingPlacement}
          >
            Sačuvaj
          </Button>,
        ]}
      >
        <p style={{ color: '#888', marginTop: 0, marginBottom: 16 }}>
          Važi za sve verzije protokola i ne kreira novu verziju. Uklanjanjem kategorije
          uklanja se i stavka te kategorije sa protokola.
        </p>
        <FloatingMultiSelect
          id="placement-categories"
          label="Kategorije"
          placeholder="Izaberite kategorije"
          options={categoryOptions}
          value={placementTarget?.categories}
          onChange={(categories) => setPlacementTarget((prev) => ({ ...prev, categories }))}
        />
        <Button
          type="link"
          style={{ paddingLeft: 0 }}
          onClick={() =>
            setPlacementTarget((prev) => ({
              ...prev,
              categories: categoryOptions.map((c) => c.value),
            }))
          }
        >
          Sve kategorije
        </Button>
        {/* Same field as the create/edit form. A bare antd InputNumber renders
            borderless here: globals.scss strips .ant-input-number's border. */}
        <div style={{ marginTop: 16 }}>
          <FloatingLabelInput
            label="Redosled (1 = prvi, prazno = na kraju)"
            type="number"
            min={1}
            value={placementTarget?.rank ?? ''}
            onChange={(e) => {
              const parsed = parseInt(e.target.value, 10);
              setPlacementTarget((prev) => ({
                ...prev,
                rank: Number.isInteger(parsed) && parsed >= 1 ? parsed : null,
              }));
            }}
          />
        </div>
      </Modal>

      <Modal
        open={!!exportTarget}
        title="Izvezi protokol"
        footer={null}
        onCancel={() => setExportTarget(null)}
      >
        {exportLoading || !exportTarget?.days ? (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <Spin />
          </div>
        ) : (
          <>
            <p style={{ marginTop: 0 }}>
              <strong>{exportTarget.name?.sr || '-'}</strong>
              {exportTarget.versionNumber && (
                <span style={{ color: '#888' }}> — v{exportTarget.versionNumber}</span>
              )}
            </p>
            <p style={{ color: '#888' }}>
              Dokument sadrži sve dane i sve aktivnosti protokola.
            </p>
            <ProtocolExportActions
              protocol={exportTarget}
              subcategoriesLabel={subcategoryLabels(exportTarget.subcategories, subcategoryOptions)}
              categoriesLabel={labelsFor(exportTarget.categories, categoryOptions)}
            />
          </>
        )}
      </Modal>
    </>
  );
}
